import { TagCategoryDialog } from "@/components/categories/tag-category-dialog";
import ErrorBoundary from '@/components/error-boundary';
import { UnifiedListLayout } from '@/components/layout/unified-list-layout';
import { IntroCard } from '@/components/shared/IntroCard';
import { UnifiedSearch } from '@/components/shared/UnifiedSearch';
import { useToast } from '@/components/ui/use-toast';
import { useProjectSettings } from '@/hooks/useProjectSettings';
import { useTagCategories } from '@/services/query/tag-categories-queries';
import { useCreateTag, useDeleteTag, useProjectTags, useUpdateTag } from '@/services/query/tags-queries';
import { TagCategory } from "@/services/supabase/tag-categories";
import { Tag } from '@/services/supabase/tags';

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';


// UI Components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
// Dropdown menu components removed as they're no longer used
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// Skeleton removed
import { Textarea } from '@/components/ui/textarea';

// Icons
import {
  AlertCircle,
  Check,
  Edit,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';

import { Skeleton } from "@/components/ui/skeleton";

// Create a logger utility for consistent logging
const logger = {
  info: (message: string, data?: unknown) => {
    const timestamp = new Date().toISOString();
    console.log(`[TagsPage][${timestamp}] INFO: ${message}`, data || '');
  },
  error: (message: string, error?: unknown) => {
    const timestamp = new Date().toISOString();
    console.error(`[TagsPage][${timestamp}] ERROR: ${message}`, error || '');
  },
  warn: (message: string, data?: unknown) => {
    const timestamp = new Date().toISOString();
    console.warn(`[TagsPage][${timestamp}] WARN: ${message}`, data || '');
  },
  debug: (message: string, data?: unknown) => {
    const timestamp = new Date().toISOString();
    console.debug(`[TagsPage][${timestamp}] DEBUG: ${message}`, data || '');
  }
};

// Performance tracking
const performance = {
  mark: (name: string) => {
    if (window.performance && window.performance.mark) {
      window.performance.mark(name);
      logger.debug(`Performance mark: ${name}`);
    }
  },
  measure: (name: string, startMark: string, endMark: string) => {
    if (window.performance && window.performance.measure) {
      try {
        window.performance.measure(name, startMark, endMark);
        const measures = window.performance.getEntriesByName(name, 'measure');
        if (measures.length > 0) {
          logger.debug(`Performance measure: ${name} = ${measures[0].duration.toFixed(2)}ms`);
        }
      } catch (e) {
        logger.error(`Failed to measure performance: ${name}`, e);
      }
    }
  }
};

// Verify Dialog component is imported correctly
logger.info('Dialog component check:', {
  isDialogDefined: typeof Dialog !== 'undefined',
  dialogType: typeof Dialog,
  dialogContent: typeof DialogContent,
  dialogHeader: typeof DialogHeader
});

// Main component
function TagsPage() {
  logger.info('TagsPage component rendering');
  performance.mark('tagsPage-render-start');
  const { projectId } = useParams<{ projectId: string }>();
  const { toast } = useToast();
  // const { projectName } = useProjectMetadata(); // Unused

  // Settings
  const { useIntroDismissal } = useProjectSettings(projectId || '');
  const { isDismissed: isIntroDismissed, dismiss: dismissIntro } = useIntroDismissal('tags');

  // State
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateCategoryDialogOpen, setIsCreateCategoryDialogOpen] = useState(false);
  const [categoryDialogMode, setCategoryDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedCategory, setSelectedCategory] = useState<TagCategory | undefined>(undefined);

  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state for tags
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [newTagCategoryId, setNewTagCategoryId] = useState<number | null>(null);



  // Queries
  const {
    data: tags = [],
    isLoading: isLoadingTags,
    isError: isTagsError,
  } = useProjectTags(projectId);

  const {
    data: categories = [],
    isLoading: isLoadingCategories,
    isError: isCategoriesError
  } = useTagCategories(projectId);

  // Mutations
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();


  // Filter tags based on search query
  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tag.description && tag.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group tags by category
  const groupedTags = useMemo(() => {
    const groups: Record<string, Tag[]> = {};

    // Add uncategorized group
    groups['Uncategorized'] = [];

    filteredTags.forEach(tag => {
      if (tag.category) {
        const categoryName = tag.category.name;
        if (!groups[categoryName]) {
          groups[categoryName] = [];
        }
        groups[categoryName].push(tag);
      } else {
        groups['Uncategorized'].push(tag);
      }
    });

    return groups;
  }, [filteredTags]);

  // Handle tag creation
  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.info('handleCreateTag function called');
    performance.mark('create-tag-start');

    if (!newTagName.trim()) {
      logger.warn('Attempted to create tag with empty name');
      toast({
        title: "Error",
        description: "Tag name is required",
        variant: "destructive",
      });
      return;
    }

    // Check if a tag with this name already exists
    const existingTag = tags.find(tag =>
      tag.name.toLowerCase() === newTagName.toLowerCase()
    );

    if (existingTag) {
      logger.warn('Attempted to create duplicate tag', {
        newTagName,
        existingTag
      });
      toast({
        title: "Error",
        description: `A tag named "${existingTag.name}" already exists`,
        variant: "destructive",
      });
      return;
    }

    try {
      logger.info('Creating tag via mutation', {
        name: newTagName,
        description: newTagDescription || undefined,
        color: '#94a3b8', // Default color
        project_id: parseInt(projectId || '0'),
        category_id: newTagCategoryId,
      });

      performance.mark('mutation-start');

      // Validate project ID
      if (!projectId) {
        throw new Error('Missing project ID');
      }

      const parsedProjectId = parseInt(projectId);
      if (isNaN(parsedProjectId)) {
        throw new Error(`Invalid project ID: ${projectId}`);
      }

      // Execute mutation
      const result = await createTagMutation.mutateAsync({
        name: newTagName,
        description: newTagDescription || undefined,
        color: '#94a3b8', // Default color
        project_id: parsedProjectId,
        category_id: newTagCategoryId,
      });

      performance.mark('mutation-end');
      performance.measure('tag-creation-mutation', 'mutation-start', 'mutation-end');

      logger.info('Tag created successfully', result);

      toast({
        title: "Success",
        description: `Tag "${newTagName}" created successfully`,
      });

      // Reset form
      try {
        logger.info('Resetting form and closing dialog');
        performance.mark('form-reset-start');

        setNewTagName('');
        setNewTagDescription('');
        setNewTagCategoryId(null);

        // Close dialog after a short delay to ensure state updates are processed
        setTimeout(() => {
          try {
            logger.info('Closing dialog after successful creation');
            setIsCreateDialogOpen(false);
            performance.mark('form-reset-end');
            performance.measure('form-reset', 'form-reset-start', 'form-reset-end');
          } catch (delayedCloseError) {
            logger.error('Error in delayed dialog close:', delayedCloseError);
            setDialogError('Error closing dialog. Please refresh the page.');
          }
        }, 100);
      } catch (resetError) {
        logger.error('Error resetting form:', resetError);
        // Try to close the dialog even if other resets fail
        try {
          setIsCreateDialogOpen(false);
        } catch (closeError) {
          logger.error('Error closing dialog:', closeError);
          setDialogError('Error closing dialog. Please refresh the page.');
        }
      }

      performance.mark('create-tag-end');
      performance.measure('create-tag-total', 'create-tag-start', 'create-tag-end');
    } catch (error: unknown) {
      logger.error('Error creating tag:', error);
      performance.mark('create-tag-error');

      // Check for specific error messages
      const errorMessage = error instanceof Error ? error.message : "Failed to create tag. Please try again.";

      // Log detailed error information
      logger.error('Tag creation failed', {
        formData: {
          name: newTagName,
          description: newTagDescription,
          color: '#94a3b8', // Default color
          project_id: projectId,
          category_id: newTagCategoryId
        },
        error: errorMessage,
        errorObject: error
      });

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Handle tag update
  const handleUpdateTag = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTag) return;

    if (!newTagName.trim()) {
      toast({
        title: "Error",
        description: "Tag name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateTagMutation.mutateAsync({
        tagId: selectedTag.id,
        updates: {
          name: newTagName,
          description: newTagDescription || null,
          // Keep the existing color from the database
          category_id: newTagCategoryId,
        },
        projectId: projectId || '',
      });

      toast({
        title: "Success",
        description: `Tag "${newTagName}" updated successfully`,
      });

      // Reset form
      try {
        console.log('Closing edit dialog');
        setIsEditDialogOpen(false);
      } catch (closeError) {
        console.error('Error closing edit dialog:', closeError);
      }
    } catch (error) {
      console.error('Error updating tag:', error);
      toast({
        title: "Error",
        description: "Failed to update tag. Please try again.",
        variant: "destructive",
      });
    }
  };



  // Handle tag deletion
  const handleDeleteTag = async () => {
    if (!selectedTag) return;

    try {
      await deleteTagMutation.mutateAsync({
        tagId: selectedTag.id,
        projectId: projectId || '',
      });

      toast({
        title: "Success",
        description: `Tag "${selectedTag.name}" deleted successfully`,
      });

      // Reset state
      try {
        console.log('Resetting delete dialog state');
        setSelectedTag(null);
        setIsDeleteDialogOpen(false);
      } catch (resetError) {
        console.error('Error resetting delete dialog state:', resetError);
        // Try to close the dialog even if other resets fail
        try {
          setIsDeleteDialogOpen(false);
        } catch (closeError) {
          console.error('Error closing delete dialog:', closeError);
        }
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Open edit dialog with selected tag data
  const openEditDialog = (tag: Tag) => {
    try {
      console.log('Opening edit dialog for tag:', tag);
      setSelectedTag(tag);
      setNewTagName(tag.name);
      setNewTagDescription(tag.description || '');
      setNewTagCategoryId(tag.category_id);
      setIsEditDialogOpen(true);
    } catch (error) {
      console.error('Error opening edit dialog:', error);
      setDialogError(`Failed to open edit dialog for tag "${tag.name}". Please try again.`);
    }
  };

  // Open delete dialog with selected tag
  const openDeleteDialog = (tag: Tag) => {
    try {
      console.log('Opening delete dialog for tag:', tag);
      setSelectedTag(tag);
      setIsDeleteDialogOpen(true);
    } catch (error) {
      console.error('Error opening delete dialog:', error);
      setDialogError(`Failed to open delete dialog for tag "${tag.name}". Please try again.`);
    }
  };

  // Add debug logging for dialog state
  React.useEffect(() => {
    console.log('Dialog state changed:', { isCreateDialogOpen, isEditDialogOpen, isDeleteDialogOpen });
  }, [isCreateDialogOpen, isEditDialogOpen, isDeleteDialogOpen]);

  // Add error boundary for dialogs
  const [dialogError, setDialogError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (dialogError) {
      toast({
        title: "Dialog Error",
        description: dialogError,
        variant: "destructive",
      });
      // Reset error after showing toast
      setDialogError(null);
    }
  }, [dialogError, toast]);

  // Handle dialog open with error catching
  const handleOpenCreateDialog = () => {
    try {
      logger.info('Starting to open create tag dialog');

      // Check component state before opening dialog
      logger.debug('Current dialog states', {
        isCreateDialogOpen,
        isEditDialogOpen,
        isDeleteDialogOpen,
        hasSelectedTag: !!selectedTag,
        categoriesCount: categories.length,
        tagsCount: tags.length
      });

      // Validate form state
      logger.debug('Current form state', {
        newTagName,
        newTagDescription,
        newTagCategoryId
      });

      // Set a timeout to detect if the state update completes
      const timeoutId = setTimeout(() => {
        logger.warn('Dialog open state change timeout - may indicate a problem');
        if (!isCreateDialogOpen) {
          logger.error('Dialog failed to open after timeout');
          setDialogError('Dialog failed to open. Please try refreshing the page.');
        }
      }, 500);

      // Mark performance before state change
      performance.mark('dialog-state-change-start');

      // Update state
      setIsCreateDialogOpen(true);

      // Clear timeout and mark performance after state change
      clearTimeout(timeoutId);
      performance.mark('dialog-state-change-end');
      performance.measure('dialog-state-change', 'dialog-state-change-start', 'dialog-state-change-end');

      logger.info('Create dialog state set to open');
    } catch (error) {
      logger.error('Error opening create tag dialog:', error);
      setDialogError('Failed to open create tag dialog. Please try again.');
    }
  };



  // Add component unmount logging
  useEffect(() => {
    return () => {
      logger.info('TagsPage component unmounting');
      performance.mark('tagsPage-unmount');
      try {
        performance.measure('tagsPage-lifetime', 'tagsPage-render-start', 'tagsPage-unmount');
      } catch (e) {
        logger.error('Error measuring component lifetime', e);
      }
    };
  }, []);

  // Add validation for categories data
  useEffect(() => {
    logger.debug('Categories data changed', {
      count: categories.length,
      isLoadingCategories,
      isCategoriesError
    });

    if (categories.length === 0 && !isLoadingCategories && !isCategoriesError) {
      logger.warn('Categories array is empty but not loading or error state');
    }

    // Validate category data structure
    categories.forEach((category, index) => {
      if (typeof category.id !== 'number') {
        logger.error(`Invalid category ID at index ${index}`, category);
      }
    });
  }, [categories, isLoadingCategories, isCategoriesError]);

  // Add a fallback UI component for dialog errors
  const DialogErrorFallback = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background p-6 rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-xl font-bold mb-4 text-destructive">Dialog Error</h2>
        <p className="mb-4">There was an error opening the dialog. This could be due to:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>A problem with your browser</li>
          <li>A conflict with another component</li>
          <li>An issue with the application state</li>
        </ul>
        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
          <Button
            variant="destructive"
            onClick={() => setDialogError(null)}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );

  // Add state tracking for dialog render attempts
  const [dialogRenderAttempt, setDialogRenderAttempt] = useState(0);

  // Add effect to track dialog render attempts
  useEffect(() => {
    if (isCreateDialogOpen) {
      logger.info(`Dialog render attempt: ${dialogRenderAttempt + 1}`);
      setDialogRenderAttempt(prev => prev + 1);

      // If we've tried to render the dialog multiple times and failed, show error
      if (dialogRenderAttempt > 2) {
        logger.error(`Dialog failed to render after ${dialogRenderAttempt} attempts`);
      }

      // Set a timeout to check if the dialog is visible
      const timeoutId = setTimeout(() => {
        // Check if dialog element exists in the DOM
        const dialogElement = document.querySelector('[role="dialog"]');
        if (!dialogElement) {
          logger.error('Dialog element not found in DOM after timeout');
          setDialogError('Dialog failed to render. Please try refreshing the page.');
        } else {
          logger.info('Dialog element found in DOM', {
            element: dialogElement.tagName,
            classes: dialogElement.className
          });
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [isCreateDialogOpen, dialogRenderAttempt]);

  return (
    <ErrorBoundary>
      {/* Show dialog error fallback if there's an error */}
      {dialogError && <DialogErrorFallback />}

      <UnifiedListLayout
        toolbar={
          <>
            <UnifiedSearch
              placeholder="Search tags..."
              value={searchQuery}
              onChange={setSearchQuery}
              className="w-full max-w-sm"
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  logger.info('New Tag button clicked');
                  performance.mark('createDialog-open-start');
                  handleOpenCreateDialog();
                }}
                className="h-10 px-6 rounded-full shadow-lg shadow-primary/20"
              >
                <Plus className="mr-2 h-4 w-4" />
                New Tag
              </Button>

            </div>
          </>
        }
      >

        {/* Intro Card */}
        {!isIntroDismissed && !isLoadingTags && tags.length === 0 && (
          <IntroCard
            title="Organize your Story Entities"
            description="Tags help you categorize your story elements across different dimensions. Use them to track themes, plot threads, character arcs, or any custom metadata you need."
            onClose={dismissIntro}
            className="mb-6"
            actions={
              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Quick Start</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Create categories like 'Themes', 'Arcs', or 'Status' to group your tags effectively.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full justify-start text-xs h-9"
                  onClick={handleOpenCreateDialog}
                >
                  <Plus className="h-3 w-3 mr-2" />
                  Create First Tag
                </Button>
              </div>
            }
          />
        )}

        <div className="space-y-8 pb-20">
          {isLoadingTags ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-border/60 overflow-hidden">
                <div className="h-12 bg-muted/5 border-b border-border/40" />
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center px-6 py-4 border-b border-border/40 last:border-0">
                    <Skeleton className="h-4 w-48" />
                    <div className="ml-auto flex gap-2">
                      <Skeleton className="h-8 w-8 rounded-md" />
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : isTagsError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center bg-destructive/5 rounded-3xl border border-destructive/10 dashed">
              <div className="bg-destructive/10 rounded-full p-4 mb-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Tags</h3>
              <p className="text-sm text-muted-foreground max-w-xs mb-6 px-4">
                We couldn't load your tags. Please try refreshing the page.
              </p>
              <Button onClick={() => window.location.reload()} variant="outline" className="h-9">
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Refresh Page
              </Button>
            </div>
          ) : filteredTags.length === 0 ? (
            searchQuery && tags.length > 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="bg-muted/30 rounded-full p-6 mb-4">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground">No tags found</h3>
                <p className="text-muted-foreground mt-1">
                  Try adjusting your search terms
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-32 text-muted-foreground animate-in zoom-in-95 duration-300">
                <div className="h-24 w-24 bg-muted/20 rounded-3xl flex items-center justify-center mb-6 border border-border/50">
                  <Plus className="h-10 w-10 text-muted-foreground/30" />
                </div>
                <p className="text-xl font-medium text-foreground">No tags yet</p>
                <p className="text-sm opacity-60 max-w-xs text-center mt-2 mb-8">
                  Create tags to organize and categorize your story elements.
                </p>
                <Button onClick={handleOpenCreateDialog} className="rounded-full px-6 shadow-lg shadow-primary/20">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Tag
                </Button>
              </div>
            )
          ) : (
            Object.entries(groupedTags).map(([category, categoryTags]) => {
              if (categoryTags.length === 0) return null;
              return (
                <div key={category} className="space-y-4 animate-in fade-in duration-500 slide-in-from-bottom-2">
                  <div className="flex items-center gap-3 px-1">
                    <h3 className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest">
                      {category}
                    </h3>
                    <div className="h-px flex-1 bg-border/40" />
                    <span className="text-[10px] font-mono text-muted-foreground/50 bg-muted/30 px-2 py-0.5 rounded-full">
                      {categoryTags.length}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-border/60 bg-card/50 backdrop-blur-sm overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted/5">
                        <TableRow className="hover:bg-transparent border-border/40 text-xs uppercase tracking-wider">
                          <TableHead className="w-[250px] pl-6 h-12 font-medium">Name</TableHead>
                          <TableHead className="hidden md:table-cell h-12 font-medium">Description</TableHead>
                          <TableHead className="w-[100px] text-right h-12 font-medium">Uses</TableHead>
                          <TableHead className="w-[100px] text-right pr-6 h-12 font-medium">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categoryTags.map((tag) => (
                          <TableRow
                            key={tag.id}
                            className="group hover:bg-muted/30 border-border/40 data-[state=selected]:bg-muted transition-colors"
                            onClick={() => openEditDialog(tag)}
                          >
                            <TableCell className="font-medium pl-6 py-4">
                              <span className="truncate max-w-[180px] block font-semibold text-foreground/80" title={tag.name}>
                                {tag.name}
                              </span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground text-sm py-4">
                              <p className="line-clamp-1 max-w-[400px]">
                                {tag.description || <span className="italic opacity-30 text-xs">No description</span>}
                              </p>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-muted-foreground py-4">
                              {tag.usage_count || 0}
                            </TableCell>
                            <TableCell className="text-right pr-4 py-4">
                              <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-background/80"
                                  onClick={() => openEditDialog(tag)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground/70 hover:bg-destructive/10 hover:text-destructive"
                                  onClick={() => openDeleteDialog(tag)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Create Tag Dialog */}
        <Dialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
        >
          <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
            <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
              <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Create New Tag</DialogTitle>
              <DialogDescription className="text-base text-muted-foreground leading-relaxed mt-1.5">
                Add a new tag to organize your story elements.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateTag}>
              <div className="space-y-6 px-8 py-8">
                <div className="space-y-2.5">
                  <Label htmlFor="create-name" className="text-sm font-medium text-foreground">Name</Label>
                  <Input
                    id="create-name"
                    placeholder="e.g. Hero's Journey, Protagonist, Setting"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                    autoFocus
                  />
                </div>

                <div className="space-y-2.5">
                  <Label htmlFor="create-description" className="text-sm font-medium text-foreground">Description (optional)</Label>
                  <Textarea
                    id="create-description"
                    placeholder="Describe what this tag represents..."
                    value={newTagDescription}
                    onChange={(e) => setNewTagDescription(e.target.value)}
                    className="min-h-[100px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all text-foreground placeholder:text-muted-foreground/50 shadow-sm resize-none"
                    rows={3}
                  />
                </div>

                <div className="space-y-2.5">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="create-category" className="text-sm font-medium text-foreground">Category</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2.5 text-xs rounded-md text-primary hover:text-primary hover:bg-primary/5"
                      onClick={(e) => {
                        e.preventDefault();
                        setCategoryDialogMode('create');
                        setSelectedCategory(undefined);
                        setIsCreateCategoryDialogOpen(true);
                      }}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      New Category
                    </Button>
                  </div>
                  <Select
                    value={newTagCategoryId ? newTagCategoryId.toString() : 'uncategorized'}
                    onValueChange={(value) => {
                      if (value === "uncategorized") {
                        setNewTagCategoryId(null);
                      } else if (value) {
                        setNewTagCategoryId(parseInt(value));
                      } else {
                        setNewTagCategoryId(null);
                      }
                    }}
                  >
                    <SelectTrigger className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground shadow-sm">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                      <SelectItem value="uncategorized">Uncategorized</SelectItem>
                      {categories.map(category => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter className="p-6 bg-muted/5 border-t border-border/40 gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsCreateDialogOpen(false)}
                  className="min-w-[100px] rounded-xl hover:bg-muted/50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createTagMutation.isPending || !newTagName.trim()}
                  className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
                >
                  {createTagMutation.isPending ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Create Tag
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Tag Dialog */}
        <Dialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            try {
              console.log('Edit dialog onOpenChange called with:', open);
              setIsEditDialogOpen(open);
            } catch (error) {
              console.error('Error in edit dialog onOpenChange:', error);
              setDialogError('Failed to change edit dialog state. Please try again.');
            }
          }}
        >
          <DialogContent
            className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0"
            onEscapeKeyDown={() => {
              console.log('Escape key pressed in edit dialog');
            }}
            onInteractOutside={() => {
              console.log('Clicked outside edit dialog');
            }}
            onPointerDownOutside={() => {
              console.log('Pointer down outside edit dialog');
            }}
          >
            <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
              <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Edit Tag</DialogTitle>
              <DialogDescription className="text-base text-muted-foreground leading-relaxed mt-1.5">
                Update the tag details.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={(e) => {
              try {
                handleUpdateTag(e);
              } catch (error) {
                console.error('Error in update tag form submit:', error);
              }
            }}>
              <div className="space-y-6 px-8 py-8">
                <div className="space-y-2.5">
                  <Label htmlFor="edit-name" className="text-sm font-medium text-foreground">Name</Label>
                  <Input
                    id="edit-name"
                    placeholder="Enter tag name"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    required
                    className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                  />
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="edit-description" className="text-sm font-medium text-foreground">Description (optional)</Label>
                  <Textarea
                    id="edit-description"
                    placeholder="Enter tag description"
                    value={newTagDescription}
                    onChange={(e) => setNewTagDescription(e.target.value)}
                    className="min-h-[100px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all text-foreground placeholder:text-muted-foreground/50 shadow-sm resize-none"
                    rows={3}
                  />
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="edit-category" className="text-sm font-medium text-foreground">Category</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2.5 text-xs rounded-md text-primary hover:text-primary hover:bg-primary/5"
                      onClick={(e) => {
                        e.preventDefault(); // Prevent form submission
                        setCategoryDialogMode('create');
                        setSelectedCategory(undefined);
                        setIsCreateCategoryDialogOpen(true);
                      }}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      New Category
                    </Button>
                  </div>
                  <div className="flex">
                    <div className="w-full">
                      <Select
                        value={newTagCategoryId ? newTagCategoryId.toString() : 'uncategorized'}
                        onValueChange={(value) => {
                          try {
                            logger.info('Edit category value changed to:', value);

                            // Handle the "uncategorized" special value
                            if (value === "uncategorized") {
                              setNewTagCategoryId(null);
                            } else if (value) {
                              // Try to parse as integer for actual category IDs
                              setNewTagCategoryId(parseInt(value));
                            } else {
                              // Fallback for empty value (should not happen with fixed SelectItem)
                              setNewTagCategoryId(null);
                            }
                          } catch (error) {
                            logger.error('Error setting category ID in edit dialog:', error);
                          }
                        }}
                      >
                        <SelectTrigger className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground shadow-sm">
                          <SelectValue placeholder="Select a category (optional)" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                          <SelectItem value="uncategorized">Uncategorized</SelectItem>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="p-6 bg-muted/5 border-t border-border/40 gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    try {
                      console.log('Edit cancel button clicked');
                      setIsEditDialogOpen(false);
                    } catch (error) {
                      console.error('Error closing edit dialog:', error);
                    }
                  }}
                  className="min-w-[100px] rounded-xl hover:bg-muted/50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateTagMutation.isPending}
                  className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
                >
                  {updateTagMutation.isPending ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Update Tag
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Tag Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            try {
              console.log('Delete dialog onOpenChange called with:', open);
              setIsDeleteDialogOpen(open);
            } catch (error) {
              console.error('Error in delete dialog onOpenChange:', error);
              setDialogError('Failed to change delete dialog state. Please try again.');
            }
          }}
        >
          <AlertDialogContent className="sm:max-w-[500px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
            <AlertDialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
              <AlertDialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Delete Tag?</AlertDialogTitle>
              <AlertDialogDescription className="text-base text-muted-foreground leading-relaxed mt-1.5">
                This action cannot be undone. This will permanently delete the tag <span className="font-medium text-foreground">"{selectedTag?.name}"</span>.
                {selectedTag?.usage_count ? (
                  <div className="mt-4 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <div className="text-sm font-medium">
                      This tag is currently used in {selectedTag.usage_count} plotcards. Deleting it will remove the tag from all these cards.
                    </div>
                  </div>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="p-6 bg-muted/5 border-t border-border/40 gap-3">
              <AlertDialogCancel
                onClick={() => {
                  try {
                    console.log('Delete cancel button clicked');
                  } catch (error) {
                    console.error('Error handling delete cancel:', error);
                  }
                }}
                className="min-w-[100px] rounded-xl hover:bg-muted/50 mt-0"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  try {
                    console.log('Delete confirm button clicked');
                    handleDeleteTag();
                  } catch (error) {
                    console.error('Error in delete tag action:', error);
                  }
                }}
                disabled={deleteTagMutation.isPending}
                className="min-w-[100px] rounded-xl font-medium shadow-sm bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-95 transition-all"
              >
                {deleteTagMutation.isPending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create/Edit Category Dialog */}
        <TagCategoryDialog
          isOpen={isCreateCategoryDialogOpen}
          onClose={() => setIsCreateCategoryDialogOpen(false)}
          mode={categoryDialogMode}
          category={selectedCategory}
          projectId={projectId ? parseInt(projectId) : 0}
        />

      </UnifiedListLayout>
    </ErrorBoundary >
  );
}

// Export the component with error boundary
export default function TagsPageWrapper() {
  return (
    <ErrorBoundary>
      <TagsPage />
    </ErrorBoundary>
  );
}
